/**
 * MessageReplyPreview - Preview da mensagem respondida dentro do bubble
 * Exibe o nome do remetente e preview do conteúdo da mensagem original
 */

import React from 'react';
import type { ConversationMessageResponse } from '@troia-v3/types';
import { Icon } from '@/shared/icons'
interface MessageReplyPreviewProps {
  reply: ConversationMessageResponse | any;  // any para suportar partial do otimista
  isOwnMessage: boolean;  // Se a mensagem ATUAL é própria (para determinar cores)
}

const MessageReplyPreview: React.FC<MessageReplyPreviewProps> = ({
  reply,
  isOwnMessage
}) => {
  // Determinar tipo de conteúdo da mensagem respondida
  const getContentPreview = (): { text: string; icon?: React.ReactNode } => {
    if (!reply.content || !Array.isArray(reply.content)) {
      return { text: reply.plainText || 'Mensagem' };
    }

    // Verificar tipo de conteúdo
    const imageContent = reply.content.find((c: any) => c.type === 'image');
    if (imageContent) {
      return {
        text: (imageContent as any).caption || 'Imagem',
        icon: <Icon name="image" className="w-3 h-3" />
      };
    }

    const videoContent = reply.content.find((c: any) => c.type === 'video');
    if (videoContent) {
      return {
        text: (videoContent as any).caption || 'Vídeo',
        icon: <Icon name="video" className="w-3 h-3" />
      };
    }

    const audioContent = reply.content.find((c: any) => c.type === 'audio');
    if (audioContent) {
      return {
        text: 'Áudio',
        icon: <Icon name="music" className="w-3 h-3" />
      };
    }

    const documentContent = reply.content.find((c: any) => c.type === 'document');
    if (documentContent) {
      return {
        text: (documentContent as any).filename || 'Documento',
        icon: <Icon name="file-text" className="w-3 h-3" />
      };
    }

    const locationContent = reply.content.find((c: any) => c.type === 'location');
    if (locationContent) {
      return {
        text: 'Localização',
        icon: <Icon name="map-pin" className="w-3 h-3" />
      };
    }

    const contactContent = reply.content.find((c: any) => c.type === 'contact');
    if (contactContent) {
      return {
        text: (contactContent as any).name || 'Contato',
        icon: <Icon name="user" className="w-3 h-3" />
      };
    }

    // Texto padrão
    const textContent = reply.content.find((c: any) => c.type === 'text');
    if (textContent && (textContent as any).text) {
      return { text: (textContent as any).text };
    }

    return { text: reply.plainText || 'Mensagem' };
  };

  const { text, icon } = getContentPreview();

  // Cores baseadas em quem enviou a mensagem ATUAL (glass vars — Task 4 P5 Glass migration)
  const bgVar = isOwnMessage
    ? 'var(--chat-bubble-reply-bg-out)'
    : 'var(--chat-bubble-reply-bg-in)';
  const accentVar = isOwnMessage
    ? 'var(--chat-bubble-reply-accent-out)'
    : 'var(--chat-bubble-reply-accent-in)';
  const senderColor = isOwnMessage
    ? 'rgba(255, 255, 255, 0.9)'
    : 'var(--chat-bubble-reply-accent-in)';
  const previewColor = isOwnMessage
    ? 'rgba(255, 255, 255, 0.8)'
    : 'var(--chat-bubble-in-fg)';
  const previewOpacity = isOwnMessage ? 1 : 0.7;

  return (
    <div
      className="rounded-md px-2 py-1.5 mb-1.5 flex items-start gap-2"
      style={{
        background: bgVar,
        borderLeft: `3px solid ${accentVar}`,
      }}
      data-testid="chat-message-reply-preview"
    >
      <div className="flex-1 min-w-0">
        {/* Nome do remetente */}
        <p
          className="text-[11.5px] font-semibold truncate mb-0.5"
          style={{ color: senderColor }}
        >
          {reply.sender?.name || reply.senderName || 'Usuário'}
        </p>

        {/* Preview do conteúdo */}
        <div
          className="flex items-center gap-1.5"
          style={{ color: previewColor, opacity: previewOpacity }}
        >
          {icon && <span>{icon}</span>}
          <p className="text-[12px] line-clamp-1 truncate">{text}</p>
        </div>
      </div>
    </div>
  );
};

export default MessageReplyPreview;
